import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import qs from 'qs';
import { API_SERVICE } from '@/api/interview-plan';
import { getScorecardInitialValues } from '@/utils/schedule-interview-helpers';
import { useInterviewStageManager } from '@/context-providers/interviewStageManager';
import { useSelector } from 'react-redux';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { getAtsJobScorecardParams } from '@/store/atsJobDetails';
import {
  findMatchingTemplateOption,
  templateOptionToFormValues,
} from '@/components/shared/scorecardTemplateDiff';

export const useScorecardTemplates = (
  questions: any,
  scorecard: {
    feedback_description: string;
    rating_description: string;
    scorecard_feedback_description: string;
    scorecard_rating_description: string;
  }
) => {
  const {
    scorecard_feedback_description,
    scorecard_rating_description,
    feedback_description,
    rating_description,
  } = scorecard || {};
  const { currentJob } = useSelector(getAtsJobCreate);
  const currentJobData = currentJob?.data || {};
  let defaultScorecardParams;
  const { department, office, employment_type } =
    currentJobData?.basic_info || {};
  const basicInfo = currentJobData?.basic_info || {};
  const { isWorkTypesEnabled } = useWorkTypeOptions();
  // The creation slice is only populated inside the job-creation wizard; on a
  // job's own settings the scope has to come from the job-details slice.
  const jobDetailsScope = useSelector(getAtsJobScorecardParams);
  if (department?.id) {
    defaultScorecardParams = {
      department_ids: [department.id],
      office_ids: office?.map((itr) => itr.id),
      // Contract C: flag ON scopes by work type id, OFF by the legacy enum.
      ...workTypeScopeParam(isWorkTypesEnabled, {
        atsWorkTypeIds: [readJobWorkTypeId(basicInfo)],
        employmentTypes: [employment_type],
      }),
    };
  } else if (jobDetailsScope?.department_ids?.length) {
    // See ReviewForm: the slice is never cleared, so an empty scope must not
    // count as one.
    defaultScorecardParams = jobDetailsScope;
  }

  const { activityAction } = useInterviewStageManager();
  const [changeTemplateInProgress, setChangeTemplateInProgress] =
    useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const templateParams = useMemo(() => {
    if (defaultScorecardParams) {
      return qs.stringify(defaultScorecardParams, {
        arrayFormat: 'brackets',
      });
    }
  }, [defaultScorecardParams]);

  const { data: scorecardTemplates = [] } = useQuery({
    queryKey: [`scorecard_templates`, templateParams],
    queryFn: () => API_SERVICE.getScoreCardTemplates(templateParams),
  });

  const templateOptions = useMemo(
    () =>
      (scorecardTemplates ?? [])
        .filter((t) => t?.id !== null && t?.id !== undefined)
        .map(
        ({
          id,
          name,
          questions: templateQuestions,
          feedback_description,
          is_feedback_mandatory,
          rating_description,
        }) => ({
          // Keyed by the template job's id; names are not unique.
          value: String(id),
          label: name,
          questions: templateQuestions,
          feedback_description: feedback_description,
          is_feedback_mandatory: is_feedback_mandatory ?? false,
          rating_description: rating_description,
        })
      ),
    [scorecardTemplates]
  );

  // `getScorecardInitialValues` mints a fresh uuid per question, so this memo
  // returns a new object on every recompute even when the content is
  // unchanged. The builder resets itself from this prop, so that churn wiped
  // each keystroke and every option deletion. Hand back the previous object
  // when only the ids differ.
  const seedRef = useRef<{ key: string; value: unknown } | null>(null);

  const initialValues = useMemo(() => {
    const templateData = selectedTemplate || {
      questions,
      feedback_description:
        feedback_description || scorecard_feedback_description,
      is_feedback_mandatory:
        (scorecard as any)?.scorecard_config?.is_feedback_mandatory ??
        (scorecard as any)?.is_feedback_mandatory ??
        false,
      rating_description: rating_description || scorecard_rating_description,
    };

    const next = {
      scoreCards: getScorecardInitialValues(templateData.questions),
      feedback_description: templateData.feedback_description || '',
      is_feedback_mandatory: templateData.is_feedback_mandatory ?? false,
      rating_description: templateData.rating_description || '',
    };
    const key = JSON.stringify(next, (k, v) => (k === 'id' ? undefined : v));
    if (seedRef.current?.key === key) {
      return seedRef.current.value as typeof next;
    }
    seedRef.current = { key, value: next };
    return next;
  }, [
    questions,
    feedback_description,
    scorecard_feedback_description,
    rating_description,
    scorecard_rating_description,
    selectedTemplate,
    scorecard,
  ]);

  const handleTemplateOnChange = useCallback(
    (selectedTemplate: {
      rating_description: string;
      feedback_description: string;
      questions: any;
    }) => {
      setChangeTemplateInProgress(true);
      setSelectedTemplate(selectedTemplate);
      setTimeout(() => setChangeTemplateInProgress(false), 100);
    },
    []
  );

  useEffect(() => {
    if (!scorecardTemplates.length || questions?.length) return;
    if (activityAction?.type !== 'new') return;

    const defaultTemplate = templateOptions[0];
    if (!defaultTemplate) return;

    const timerId = setTimeout(() => {
      handleTemplateOnChange(defaultTemplate);
    }, 0);
    return () => clearTimeout(timerId);
  }, [
    scorecardTemplates,
    questions?.length,
    activityAction?.type,
    templateOptions,
    handleTemplateOnChange,
  ]);

  // The template a saved scorecard still corresponds to: the recorded source
  // first, else questions that match one verbatim. Drives the picker's value
  // so an untouched scorecard names its template instead of reading blank.
  const resolvedTemplate = useMemo(() => {
    if (selectedTemplate) return selectedTemplate;
    const source = (scorecard as { source_ats_job_id?: number | string | null })
      ?.source_ats_job_id;
    if (source !== null && source !== undefined) {
      const byId = templateOptions.find((o) => o.value === String(source));
      if (byId) return byId;
    }
    return findMatchingTemplateOption(
      templateOptions,
      templateOptionToFormValues({
        questions,
        feedback_description:
          feedback_description || scorecard_feedback_description,
        rating_description: rating_description || scorecard_rating_description,
        is_feedback_mandatory: (scorecard as any)?.is_feedback_mandatory,
      })
    );
  }, [
    selectedTemplate,
    templateOptions,
    scorecard,
    questions,
    feedback_description,
    scorecard_feedback_description,
    rating_description,
    scorecard_rating_description,
  ]);

  return {
    templateOptions,
    handleTemplateOnChange,
    initialValues,
    changeTemplateInProgress,
    /** Picker value; null when the questions match no offered template. */
    selectedTemplateValue: (resolvedTemplate as any)?.value ?? null,
    /** The resolved option itself, for diffing the builder against it. */
    selectedTemplateOption: resolvedTemplate ?? null,
    /** True once the questions correspond to no offered template. */
    isCustomScorecard: !resolvedTemplate && (questions?.length ?? 0) > 0,
    /** Whether the user picked this template in this session, as opposed to it
     *  being recognised from the saved questions. */
    isManualPick: !!selectedTemplate,
  };
};
import {
  readJobWorkTypeId,
  useWorkTypeOptions,
  workTypeScopeParam,
} from '@/api/work-types';
